import { forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import { SectionHeader } from './ui/StyledComponents'
import { 
  TextField, 
  FormControl, 
  Select, 
  MenuItem, 
  InputLabel, 
  Grid,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import QuranProgressFields from './QuranProgressFields'
import supabase from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import { updateCurrentTeacherActivity } from '../utils/teacherActivity'

const StudentQuranProgress = forwardRef(function StudentQuranProgress({ studentId, classId, date }, ref) {
  const [startingSurah, setStartingSurah] = useState('')
  const [startingVerse, setStartingVerse] = useState('')
  const [endingSurah, setEndingSurah] = useState('')
  const [endingVerse, setEndingVerse] = useState('')
  const [assignmentStatus, setAssignmentStatus] = useState(null)
  const [reviewCompleted, setReviewCompleted] = useState(null)
  const [notes, setNotes] = useState('')
  const { user, userRole } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    if (startingSurah && !startingVerse) {
      setStartingVerse('')
    }
  }, [startingSurah, startingVerse])

  useEffect(() => {
    if (endingSurah && !endingVerse) {
      setEndingVerse('')
    }
  }, [endingSurah, endingVerse])

  useEffect(() => {
    const fetchQuranProgress = async () => {
      const { data, error } = await supabase
        .from('student_quran_progress')
        .select('*')
        .eq('student_id', studentId)
        .eq('class_id', classId)
        .eq('date', date.format('YYYY-MM-DD'))
        .maybeSingle()

      if (error || !data) {
        setStartingSurah('')
        setStartingVerse('')
        setEndingSurah('')
        setEndingVerse('')
        setAssignmentStatus(null)
        setReviewCompleted(false)
        setNotes('')
      } else {
        setStartingSurah(data.starting_surah)
        setStartingVerse(data.starting_verse)
        setEndingSurah(data.ending_surah)
        setEndingVerse(data.ending_verse)
        setAssignmentStatus(data.assignment_status)
        setReviewCompleted(data.review_completed)
        setNotes(data.notes || '')
      }
    }

    if (studentId && classId) {
      fetchQuranProgress()
    }
  }, [studentId, classId, date])

  const saveQuranProgress = async () => {
    const formattedDate = date.format('YYYY-MM-DD')
    
    const { error } = await supabase
      .from('student_quran_progress')
      .upsert(
        [
          {
            student_id: studentId,
            class_id: classId,
            date: formattedDate,
            starting_surah: startingSurah || null,
            starting_verse: startingVerse || null,
            ending_surah: endingSurah || null,
            ending_verse: endingVerse || null,
            assignment_status: assignmentStatus,
            review_completed: reviewCompleted || null,
            notes: notes || null,
          },
        ],
        { onConflict: ['student_id', 'date', 'class_id'] },
      )

    // Update teacher activity tracking if save was successful
    if (!error) {
      await updateCurrentTeacherActivity(user, userRole)
    }

    return { error }
  }

  useImperativeHandle(ref, () => ({
    saveQuranProgress,
  }))

  return (
    <Grid container spacing={{ xs: 2, sm: 2 }}>
      <Grid item xs={12}>
        <SectionHeader 
          variant="h6"
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          Quran Progress
        </SectionHeader>
        <Grid container spacing={{ xs: 2, sm: 2 }}>
          <Grid item xs={12}>
            <QuranProgressFields
              surahLabel="Starting Surah"
              verseLabel="Starting Verse"
              surah={startingSurah}
              verse={startingVerse}
              onSurahChange={(e) => setStartingSurah(e.target.value)}
              onVerseChange={(e) => setStartingVerse(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <QuranProgressFields
              surahLabel="Ending Surah"
              verseLabel="Ending Verse"
              onSurahChange={(e) => setEndingSurah(e.target.value)}
              onVerseChange={(e) => setEndingVerse(e.target.value)}
              surah={endingSurah}
              verse={endingVerse}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              variant="outlined"
              placeholder="Add notes about student's Quran progress..."
              sx={{ 
                mb: { xs: 1.5, sm: 2 },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={{ xs: 2, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel 
                    id="assignment-status-label"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Assignment Status
                  </InputLabel>
                  <Select
                    labelId="assignment-status-label"
                    id="assignment-status"
                    value={assignmentStatus || ''}
                    label="Assignment Status"
                    onChange={(e) => setAssignmentStatus(e.target.value || null)}
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '& .MuiMenuItem-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Not Selected</em>
                    </MenuItem>
                    <MenuItem value="full">Full</MenuItem>
                    <MenuItem value="partial">Partial</MenuItem>
                    <MenuItem value="none">None</MenuItem>

                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth={isMobile}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={reviewCompleted}
                        onChange={(e) => setReviewCompleted(e.target.checked)}
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          },
                        }}
                      />
                    }
                    label="Review Completed"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
})

export default StudentQuranProgress